import { useCallback, useState, useEffect } from 'react';
import { Upload, X, Image, Film } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface MediaUploaderProps {
  type: 'photo' | 'video';
  file: File | null;
  onFileChange: (file: File | null) => void;
  disabled?: boolean;
}

const PHOTO_ACCEPT = 'image/*,.heic,.heif';
const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/webm';

function isHeicFile(file: File): boolean {
  const mimeType = file.type.toLowerCase();
  const filename = file.name.toLowerCase();

  return (
    mimeType === 'image/heic' ||
    mimeType === 'image/heif' ||
    mimeType === 'image/heic-sequence' ||
    mimeType === 'image/heif-sequence' ||
    filename.endsWith('.heic') ||
    filename.endsWith('.heif')
  );
}

export function MediaUploader({
  type,
  file,
  onFileChange,
  disabled,
}: MediaUploaderProps) {
  const [preview, setPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const accept = type === 'photo' ? PHOTO_ACCEPT : VIDEO_ACCEPT;

  // Generate preview when file is provided via props (e.g., from share intent)
  useEffect(() => {
    if (file && !preview) {
      if (type === 'photo') {
        if (isHeicFile(file)) {
          setPreview(null);
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          setPreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setPreview(URL.createObjectURL(file));
      }
    } else if (!file && preview) {
      // Clean up preview when file is cleared
      if (type === 'video' && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
      setPreview(null);
    }
  }, [file, preview, type]);

  const handleFile = useCallback((selectedFile: File) => {
    // Validate file type
    const isValid = type === 'photo'
      ? selectedFile.type.startsWith('image/') || isHeicFile(selectedFile)
      : selectedFile.type.startsWith('video/');

    if (!isValid) {
      alert(`Please select a valid ${type} file`);
      return;
    }

    onFileChange(selectedFile);

    // Create preview
    if (type === 'photo') {
      if (isHeicFile(selectedFile)) {
        setPreview(null);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(URL.createObjectURL(selectedFile));
    }
  }, [type, onFileChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFile(droppedFile);
    }
  }, [handleFile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFile(selectedFile);
    }
  };

  const clearFile = () => {
    if (preview && type === 'video') {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    onFileChange(null);
  };

  const Icon = type === 'photo' ? Image : Film;

  return (
    <div className="space-y-4">
      {!file ? (
        <label
          className={cn(
            'flex flex-col items-center justify-center gap-4 p-8 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200',
            isDragging
              ? 'border-primary bg-primary/10'
              : 'border-border hover:border-primary/50 hover:bg-card',
            disabled && 'opacity-50 pointer-events-none'
          )}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept={accept}
            onChange={handleInputChange}
            disabled={disabled}
            className="hidden"
          />
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <Upload className="w-8 h-8 text-muted-foreground" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium">
              Drop {type} here or tap to select
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {type === 'photo' ? 'JPG, PNG, GIF, WebP, HEIC' : 'MP4, MOV, WebM'}
            </p>
          </div>
        </label>
      ) : (
        <div className="relative rounded-xl overflow-hidden bg-card animate-in">
          {type === 'photo' && preview && (
            <img
              src={preview}
              alt="Preview"
              className="w-full max-h-80 object-contain bg-black"
            />
          )}
          {type === 'photo' && !preview && file && (
            <div className="min-h-48 flex flex-col items-center justify-center gap-3 bg-black px-6 py-8 text-center">
              <Image className="w-10 h-10 text-muted-foreground" />
              <p className="max-w-full truncate text-sm font-medium">{file.name}</p>
            </div>
          )}
          {type === 'video' && preview && (
            <video
              src={preview}
              controls
              className="w-full max-h-80 bg-black"
            />
          )}

          <div className="absolute top-2 right-2 flex gap-2">
            <Button
              type="button"
              variant="secondary"
              size="icon"
              onClick={clearFile}
              disabled={disabled}
              className="bg-black/50 hover:bg-black/70 backdrop-blur-sm"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="p-3 flex items-center gap-2 border-t border-border">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm truncate flex-1">{file.name}</span>
            <span className="text-xs text-muted-foreground">
              {(file.size / 1024 / 1024).toFixed(1)} MB
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
